import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  FormHelperText,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";

export default function CodeFormDialog({
  open,
  onClose,
  initial,
  onSave,
  saving,
  toolboxes,
}) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [code, setCode] = useState(initial?.code || "");
  const [toolboxIds, setToolboxIds] = useState(initial?.toolboxes_enabled_ids || []);

  useEffect(() => {
    setName(initial?.name || "");
    setDescription(initial?.description || "");
    setCode(initial?.code || "");
    setToolboxIds(initial?.toolboxes_enabled_ids || []);
  }, [initial, open]);

  const handleSubmit = () => {
    const formData = {
      _id: initial?._id,
      name: name.trim(),
      description: description.trim(),
      code: code.trim(),
      toolboxes_enabled_ids: toolboxIds,
    };
    onSave(formData);
  };

  const isValid = name.trim() && code.trim() && (toolboxIds?.length || 0) > 0;

  const byId = useMemo(
    () => Object.fromEntries((toolboxes || []).map((t) => [t._id, t])),
    [toolboxes]
  );

  const handleRemoveToolbox = (id) => {
    setToolboxIds((prev) => prev.filter((x) => x !== id));
  };

  const selectedChips = (selected) => (
    <Box display="flex" flexWrap="wrap" gridGap={8}>
      {(selected || []).map((id) => {
        const label = byId[id]?.toolbox_name || id;
        return (
          <Chip
            key={id}
            label={label}
            onDelete={() => handleRemoveToolbox(id)}
            // prevent the Select from opening when clicking the chip delete icon
            onMouseDown={(e) => e.stopPropagation()}
            color="quCreate"
            variant="default"
          />
        );
      })}
    </Box>
  );

  // Hide already-selected toolboxes from the dropdown
  const menuToolboxes = (toolboxes || []).filter((tb) => !toolboxIds.includes(tb._id));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initial?._id ? "Edit Code Mapping" : "New Code Mapping"}
      </DialogTitle>

      <DialogContent>
        <Box display="grid" gridGap={16}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            label="Access Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
            required
          />

          <FormControl fullWidth>
            <InputLabel id="toolboxes-select-label">Toolboxes Enabled</InputLabel>
            <Select
              labelId="toolboxes-select-label"
              multiple
              value={toolboxIds}
              onChange={(e) => setToolboxIds(e.target.value)}
              renderValue={selectedChips}
              displayEmpty
            >
              {menuToolboxes.map((tb) => (
                <MenuItem key={tb._id} value={tb._id}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Box>
                      <ListItemText
                        primary={tb.toolbox_name}
                        secondary={(tb.components || []).join(", ")}
                      />
                    </Box>
                    <Box ml={2}>
                      <Chip size="small" label={`${(tb.components || []).length} comps`} />
                    </Box>
                  </Box>
                </MenuItem>
              ))}
              {menuToolboxes.length === 0 && (
                <MenuItem disabled>
                  <ListItemText primary="All toolboxes selected" />
                </MenuItem>
              )}
            </Select>
            <FormHelperText>
              Select one or more toolboxes. Click the “×” on a chip to remove it.
            </FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="quCreate"
          variant="contained"
          disabled={!isValid || saving}
        >
          {saving ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
